import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, Checkbox, CodeField, Dialog, EmptyState, Stack, t, Text, TextField, toast } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { ListTable } from './ListTable';
import { api, ApiFailure, unwrap } from '../lib/api';

type Task = maintenance.components['schemas']['DeclaredTask'];
type Operation = maintenance.components['schemas']['InstallationOperation'];

/**
 * SPEC-034 § 12.2 (lot 1.5, slice 3): the tasks declared in deploy/tasks.yaml
 * of the release in service, run by hand — typed arguments, never a shell; a
 * double confirmation when the task asks for it; the output masked on the server.
 */
export function EnvironmentTasks({ installation, production, onStepUp }: { installation: string; production: boolean; onStepUp: () => void }) {
  const [running, setRunning] = useState<Task | null>(null);
  const [args, setArgs] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState(false);
  const [sending, setSending] = useState(false);
  const [operation, setOperation] = useState<{ id: string; task: string } | null>(null);
  const op = useQuery({
    queryKey: ['environment-task-run', installation, operation?.id],
    queryFn: async () => unwrap(await api.maintenance.GET('/installations/{id}/operations/{operation}', { params: { path: { id: installation, operation: operation!.id } } })) as Operation,
    enabled: Boolean(operation),
    refetchInterval: q => (q.state.data?.status === 'running' || !q.state.data ? 2_000 : false),
  });

  const open = (task: Task) => { setArgs({}); setConfirmed(false); setRunning(task); };
  const run = async () => {
    if (!running) return;
    setSending(true);
    try {
      const r = unwrap(await api.maintenance.POST('/installations/{id}/tasks/{task}', {
        params: { path: { id: installation, task: running.name } },
        body: { args, ...(running.confirm ? { confirm: confirmed } : {}) },
      }));
      setOperation({ id: r.operation, task: running.name });
      setRunning(null);
      toast({ tone: 'info', title: t('environment.tasks.started', { task: running.name }) });
    } catch (err) {
      if (err instanceof ApiFailure && err.body.code === 'MFA_STEP_UP_REQUIRED') { setRunning(null); onStepUp(); return; }
      toast({ tone: 'neg', title: err instanceof ApiFailure ? err.body.message : String(err), description: err instanceof ApiFailure ? err.body.hint : undefined });
    } finally {
      setSending(false);
    }
  };

  const result = op.data;
  return (
    <Stack gap={16}>
      <Text variant="body-sm" tone="mute">{t('environment.tasks.lead')}</Text>
      {/* D120: searched, filtered and paged by the server, as every list. */}
      <ListTable<Task>
        label={t('environment.tasks.table')}
        queryKey={['environment-tasks', installation]}
        fetch={async query => unwrap(await api.maintenance.GET('/installations/{id}/tasks', { params: { path: { id: installation }, query: query as never } }))}
        rowKey="name"
        defaultSort={{ key: 'name', dir: 'asc' }}
        search={t('environment.tasks.search')}
        facets={[
          { key: 'service', label: t('environment.tasks.service') },
          { key: 'run', label: t('environment.tasks.when'), valueLabel: v => (v === 'manual' ? t('environment.tasks.byHand') : t(`environment.tasks.runs.${v}`)) },
        ]}
        refetchInterval={false}
        empty={<EmptyState framed={false} icon="terminal" title={t('environment.tasks.empty')} />}
        columns={[
          {
            key: 'name', label: t('environment.tasks.name'), grow: true,
            render: (r: Task) => (
              <Stack gap={4}>
                <Text variant="code">{r.name}</Text>
                {r.description && <Text variant="caption" tone="mute">{r.description}</Text>}
              </Stack>
            ),
          },
          { key: 'service', label: t('environment.tasks.service'), render: (r: Task) => <Text variant="code">{r.service}</Text> },
          {
            key: 'run', label: t('environment.tasks.when'), sortable: false,
            render: (r: Task) => (r.run ? <Badge tone="info" size="sm">{t(`environment.tasks.runs.${r.run}`)}</Badge> : <Text variant="body-sm" tone="mute">{t('environment.tasks.byHand')}</Text>),
          },
          {
            key: 'actions', label: '', pinned: 'right' as const, sortable: false,
            render: (r: Task) => <Button variant="ghost" size="sm" icon="play_arrow" onClick={() => open(r)}>{t('environment.tasks.run')}</Button>,
          },
        ]}
      />

      {result && operation && (
        <Stack gap={8}>
          {result.status === 'running' && <Text variant="body-sm" tone="mute">{t('environment.tasks.running', { task: operation.task })}</Text>}
          {result.status === 'failed' && <Alert tone="neg" title={t('environment.tasks.failed', { task: operation.task })}>{result.failure}</Alert>}
          {result.status === 'succeeded' && <Alert tone="pos" title={t('environment.tasks.succeeded', { task: operation.task })} />}
          {result.status !== 'running' && (
            <CodeField label={t('environment.tasks.output', { task: operation.task })} value={result.lines.join('\n') || t('environment.logs.empty')} readOnly lineNumbers maxHeight={420} />
          )}
        </Stack>
      )}

      <Dialog
        open={running !== null}
        onClose={() => setRunning(null)}
        title={t('environment.tasks.dialogTitle', { task: running?.name ?? '' })}
        description={running ? `${running.description ?? ''}${running.description ? ' ' : ''}${t(production ? 'environment.tasks.dialogBodyProduction' : 'environment.tasks.dialogBody', { command: [...running.command, ...running.args.map(a => `<${a.name}>`)].join(' ') })}` : ''}
        confirmLabel={t('environment.tasks.runConfirm', { task: running?.name ?? '' })}
        confirmDisabled={sending || Boolean(running?.confirm && !confirmed) || Boolean(running?.args.some(a => a.required && !(args[a.name] ?? '').trim()))}
        onConfirm={() => void run()}
      >
        {running && (
          <Stack gap={12}>
            {running.args.map(a => (
              <TextField key={a.name} id={`task-arg-${a.name}`} label={a.name} value={args[a.name] ?? ''} mono
                hint={t(`environment.tasks.types.${a.type}`)} required={a.required}
                onChange={e => setArgs({ ...args, [a.name]: e.target.value })} />
            ))}
            {running.confirm && <Checkbox checked={confirmed} onChange={(on: boolean) => setConfirmed(on)} label={t('environment.tasks.confirm', { task: running.name })} />}
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
